import React, {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { SubscriptionOptions } from "@reduxjs/toolkit/query";
import { format } from "date-fns";

import { PlaceholderCard, TradeDataExportModal } from "@/components";
import Pagination from "@/components/paginations/pagination";
import { tableSx, tableContainerSx } from "@/components/table/table-styles";
import { crash_warning } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  startPolling,
  stopPolling,
  tradeDataApi,
  useGetEntrySummariesQuery,
  useGetHTSCodesForDropdownQuery,
  useLazyGetAllTradeDataLogsQuery,
  useLazyGetClusterAuditQuery,
  usePostTradeDataMutation,
} from "@/services";
import { useSnackbar } from "@/hooks";
import { roles } from "@/roles";
import { getPollingInterval } from "@/utils";
import AuditDetailModal from "@/components/audit-detail-modal";
import ProcessBox from "./ProcessBox";
import UploadData from "./upload-data";

import { EntrySummaryRow as EntrySummaryRowType } from "./hooks/useEntrySummaries";
import EntrySummaryRowComponent from "./components/EntrySummaryRow";
import DocumentsDrawer from "./components/DocumentsDrawer";
import EntrySummaryFilters, {
  EntrySummaryFiltersState,
  initialEntrySummaryFilters,
} from "./components/EntrySummaryFilters";
import {
  StyledMenuPopover,
  Backdrop,
} from "./style";
import { IOSSwitch } from "@/components/switch/styles";
import {
  GridView as ColumnsIcon,
  FileDownload as ExportIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

const INITIAL_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;

interface SummaryColumnDef {
  key: string;
  label: string;
  width?: string;
  align: "left" | "center" | "right";
}

// Base summary columns (always shown)
const baseSummaryColumns: SummaryColumnDef[] = [
  { key: "expand", label: "", width: "48px", align: "center" as const },
  { key: "entrySummaryNumber", label: "Entry #", align: "left" as const },
  { key: "entryDate", label: "Entry Date", align: "center" as const },
  { key: "portOfEntryCode", label: "Port", align: "center" as const },
  { key: "lineCount", label: "Lines", align: "center" as const },
  { key: "totalGoodsValue", label: "Total Value", align: "right" as const },
  { key: "totalLineDuties", label: "Total Duties", align: "right" as const },
  { key: "documentCount", label: "Documents", align: "center" as const },
  { key: "latestAuditStatus", label: "Status", align: "center" as const },
];

// Optional summary columns — shown only when data is present
const optionalSummaryColumns: SummaryColumnDef[] = [
  { key: "liquidationStatus", label: "Liquidation Status", align: "center" as const },
  { key: "liquidationDate", label: "Liquidation Date", align: "center" as const },
  { key: "entrySummaryVersionNumber", label: "Version", align: "center" as const },
];

// Default line columns to show when expanded
const defaultLineColumns = [
  "entrySummaryLineNumber",
  "htsNumberFull",
  "manufacturerID",
  "countryOfOriginCode",
  "lineTariffGoodsValueAmount",
  "totalLineDuties",
];

const EntrySummaryView: FC = (): ReactElement => {
  // Pagination state
  const [page, setPage] = useState(INITIAL_PAGE);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);

  // Column visibility state
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [visibleLineColumns, setVisibleLineColumns] = useState<string[]>(defaultLineColumns);

  // Expansion state
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  // Filters state
  const [filters, setFilters] = useState<EntrySummaryFiltersState>(
    initialEntrySummaryFilters
  );

  // Documents drawer state
  const [documentsDrawer, setDocumentsDrawer] = useState<{
    open: boolean;
    summary: EntrySummaryRowType | null;
  }>({
    open: false,
    summary: null,
  });

  // Audit modal state
  const [auditModal, setAuditModal] = useState<{
    open: boolean;
    audit: ClusterAudit | null;
    loading: boolean;
    entrySummaryNumber: string;
  }>({
    open: false,
    audit: null,
    loading: false,
    entrySummaryNumber: "",
  });

  // Export modal state
  const [showExportModal, setShowExportModal] = useState<boolean>(false);

  // Upload state
  const [showUploadDataModal, setShowUploadDataModal] = useState<boolean>(false);
  const [timeStamp, setTimeStamp] = useState<string>(new Date().toISOString());
  const progressSubscriberRef = useRef<
    ((options: SubscriptionOptions) => void) | null
  >(null);

  // Hooks
  const { show } = useSnackbar();
  const dispatch = useAppDispatch();
  const [getClusterAudit] = useLazyGetClusterAuditQuery();
  const [postTradeData, { isLoading: isCreatingTradeData }] = usePostTradeDataMutation();
  const [fetchUploadProgress, { data: uploadProgress, error: progressError }] =
    useLazyGetAllTradeDataLogsQuery();
  const { isPollingActive } = useAppSelector((state) => state.polling);

  // Redux selectors
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);

  // Determine if we should skip API calls
  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const shouldSkip = useMemo(() => {
    if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return false;
    }
    return canSelectClients && !selectedClient;
  }, [canSelectClients, selectedClient, selectedTenant, user]);

  const isAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  }, [user, selectedTenant]);

  const isTDAdmin = useMemo(() => user?.userRole === roles.TDADMIN, [user]);

  const clientIdForDropdowns = useMemo(() => {
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return selectedTenant?._id;
    }
    return undefined;
  }, [selectedClient?._id, user?.userRole, selectedTenant?._id, selectedTenant?.tenantType]);

  // Build backend filters from filter state
  const backendFilters = useMemo(() => {
    const f: Record<string, any> = {};

    if (filters.entrySummaryNumber.value) {
      f.entrySummaryNumber = {
        value: filters.entrySummaryNumber.value,
        operator: filters.entrySummaryNumber.operator,
      };
    }
    if (filters.portOfEntry.length > 0) f.portOfEntry = filters.portOfEntry;
    if (filters.countryOfOrigin.length > 0) f.countryOfOrigin = filters.countryOfOrigin;
    if (filters.htsCode.length > 0) f.htsCode = filters.htsCode;
    if (filters.manufacturerID.length > 0) f.manufacturerID = filters.manufacturerID;
    if (filters.filerCode.value) {
      f.filerCode = {
        value: filters.filerCode.value,
        operator: filters.filerCode.operator,
      };
    }
    if (filters.commercialDescription.value) {
      f.commercialDescription = {
        value: filters.commercialDescription.value,
        operator: filters.commercialDescription.operator,
      };
    }

    if (filters.valueMin) { const v = parseFloat(filters.valueMin); if (!isNaN(v)) f.valueMin = v; }
    if (filters.valueMax) { const v = parseFloat(filters.valueMax); if (!isNaN(v)) f.valueMax = v; }
    if (filters.dutyMin) { const v = parseFloat(filters.dutyMin); if (!isNaN(v)) f.dutyMin = v; }
    if (filters.dutyMax) { const v = parseFloat(filters.dutyMax); if (!isNaN(v)) f.dutyMax = v; }
    if (filters.mfnDutyMin) { const v = parseFloat(filters.mfnDutyMin); if (!isNaN(v)) f.mfnDutyMin = v; }
    if (filters.mfnDutyMax) { const v = parseFloat(filters.mfnDutyMax); if (!isNaN(v)) f.mfnDutyMax = v; }
    if (filters.duty201Min) { const v = parseFloat(filters.duty201Min); if (!isNaN(v)) f.duty201Min = v; }
    if (filters.duty201Max) { const v = parseFloat(filters.duty201Max); if (!isNaN(v)) f.duty201Max = v; }
    if (filters.duty232Min) { const v = parseFloat(filters.duty232Min); if (!isNaN(v)) f.duty232Min = v; }
    if (filters.duty232Max) { const v = parseFloat(filters.duty232Max); if (!isNaN(v)) f.duty232Max = v; }
    if (filters.duty301Min) { const v = parseFloat(filters.duty301Min); if (!isNaN(v)) f.duty301Min = v; }
    if (filters.duty301Max) { const v = parseFloat(filters.duty301Max); if (!isNaN(v)) f.duty301Max = v; }

    if (filters.status) f.auditStatus = filters.status;
    if (filters.liquidationStatus.length > 0) f.liquidationStatus = filters.liquidationStatus;

    return Object.keys(f).length > 0 ? f : undefined;
  }, [filters]);

  // Fetch entry summaries with server-side grouping & pagination
  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetEntrySummariesQuery(
    {
      pageSize: limit,
      pageNo: page,
      ...(filters.startDate && { dateFrom: format(filters.startDate, "yyyy-MM-dd") }),
      ...(filters.endDate && { dateTo: format(filters.endDate, "yyyy-MM-dd") }),
      sortType: filters.sort.direction === "asc" ? 1 : -1,
      sortBy: filters.sort.field === "entryDate" ? "entryDateSeconds" : filters.sort.field,
      ...(user?.userRole &&
        [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
          clientId: selectedClient?._id,
        }),
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
      ...(backendFilters && { filters: backendFilters }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  const { data: htsOptions = [] } = useGetHTSCodesForDropdownQuery(
    {
      clientId: clientIdForDropdowns,
      ...(isAdmin && { tenantId: selectedTenant?._id }),
      includeChapter99: false,
    },
    { skip: shouldSkip }
  );

  // Derive display data from server response
  const entrySummaries = useMemo(() => {
    const data = apiData?.getEntrySummaries?.data ?? [];
    return data.map((summary: any) => ({
      ...summary,
      lines: (summary.lines || []).map((line: any) => ({
        ...line,
        totalLineDuties:
          (line.mfnDuty ?? 0) +
          (line.duty201 ?? 0) +
          (line.duty232 ?? 0) +
          (line.duty301 ?? 0) +
          (line.oth99Duties ?? 0),
      })),
    }));
  }, [apiData]);

  const totalEntries = apiData?.getEntrySummaries?.meta?.countDocs ?? 0;

  // Build summary columns: base + optional columns that have data in the current page
  const summaryColumns = useMemo(() => {
    const activeOptional = optionalSummaryColumns.filter((col) =>
      entrySummaries.some((entry: any) => {
        const val = entry[col.key];
        return val != null && val !== "" && val !== 0;
      })
    );
    return [...baseSummaryColumns, ...activeOptional];
  }, [entrySummaries]);

  const SUMMARY_COLUMN_COUNT = summaryColumns.length;

  // Filter options from backend
  const availablePorts = apiData?.getEntrySummaries?.filterOptions?.availablePorts ?? [];
  const availableCountries = apiData?.getEntrySummaries?.filterOptions?.availableCountries ?? [];
  const availableManufacturers = apiData?.getEntrySummaries?.filterOptions?.availableManufacturers ?? [];
  const availableFilerCodes = apiData?.getEntrySummaries?.filterOptions?.availableFilerCodes ?? [];
  const availableLiquidationStatuses = apiData?.getEntrySummaries?.filterOptions?.availableLiquidationStatuses ?? [];

  // HTS codes still from dedicated dropdown query
  const availableHtsCodes = useMemo(() => {
    return (htsOptions as any[])
      .filter((option: any) => option.type === "hts")
      .map((option: any) => ({
        value: option.value,
        label: option.label || option.value,
      }));
  }, [htsOptions]);

  // Handlers
  const handleFiltersChange = useCallback(
    (newFilters: EntrySummaryFiltersState) => {
      setFilters(newFilters);
      setPage(INITIAL_PAGE);
    },
    []
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setExpandedEntries(new Set()); // Collapse all on page change
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(INITIAL_PAGE);
    setExpandedEntries(new Set());
  };

  const handleColumnsMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnsMenuAnchor(event.currentTarget);
  };

  const handleColumnsMenuClose = () => {
    setColumnsMenuAnchor(null);
  };

  const handleToggleLineColumn = (columnKey: string) => {
    setVisibleLineColumns((prev) => {
      if (prev.includes(columnKey)) {
        // Don't allow removing last column
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const toggleEntry = (entrySummaryNumber: string) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(entrySummaryNumber)) {
        next.delete(entrySummaryNumber);
      } else {
        next.add(entrySummaryNumber);
      }
      return next;
    });
  };

  const handleDocumentsClick = (summary: EntrySummaryRowType) => {
    setDocumentsDrawer({
      open: true,
      summary,
    });
  };

  const handleDocumentsDrawerClose = () => {
    setDocumentsDrawer({
      open: false,
      summary: null,
    });
  };

  // Handle status click to view audit
  const handleStatusClick = async (summary: EntrySummaryRowType) => {
    if (!summary.clusterId) {
      show("No Audit Available", "No cluster associated with this entry", "info");
      return;
    }

    setAuditModal({
      open: true,
      audit: null,
      loading: true,
      entrySummaryNumber: summary.entrySummaryNumber,
    });

    try {
      const result = await getClusterAudit({
        clusterId: summary.clusterId,
        tenantId: isTDAdmin && selectedTenant ? selectedTenant._id : undefined,
      }).unwrap();

      if (result) {
        setAuditModal((prev) => ({
          ...prev,
          audit: result,
          loading: false,
        }));
      }
    } catch (error) {
      show("Error", "Failed to load audit details", "error");
      setAuditModal((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleAuditModalClose = () => {
    setAuditModal({
      open: false,
      audit: null,
      loading: false,
      entrySummaryNumber: "",
    });
  };

  // Upload permission check
  const canUploadData = useMemo(() => {
    return (
      !!user &&
      [roles.SPOWNER, roles.SPPARTNER, roles.TDCLIENT, roles.TDADMIN].includes(user.userRole)
    );
  }, [user]);

  // Upload progress polling
  const pullProgress = useCallback(async () => {
    const pollingInterval = uploadProgress ? getPollingInterval([uploadProgress]) : 15000;

    const progressSubscriber = fetchUploadProgress({
      input: {
        clientId: selectedClient?._id ?? user?._id ?? "",
        uploadTime: timeStamp,
        ...(user?.userRole === roles.TDADMIN && selectedTenant && {
          tenantId: selectedTenant._id,
        }),
      },
    });

    progressSubscriber.updateSubscriptionOptions({ pollingInterval });
    dispatch(startPolling());
    progressSubscriberRef.current = progressSubscriber.updateSubscriptionOptions;
  }, [fetchUploadProgress, selectedClient, dispatch, timeStamp, uploadProgress, user, selectedTenant]);

  // Polling effects
  useEffect(() => {
    if (isPollingActive) {
      pullProgress();
    }
  }, [isPollingActive, pullProgress]);

  useEffect(() => {
    if (
      progressSubscriberRef.current &&
      (progressError || uploadProgress?.progress === 100)
    ) {
      progressSubscriberRef.current({ pollingInterval: 0 });
      progressSubscriberRef.current = null;
      dispatch(stopPolling());
    }

    if (uploadProgress?.progress === 100) {
      dispatch(tradeDataApi.util.invalidateTags(["tradeData"]));
      setPage(INITIAL_PAGE);
    }
  }, [uploadProgress, progressError, dispatch]);

  useEffect(() => {
    if (progressSubscriberRef.current && uploadProgress) {
      const newPollingInterval = getPollingInterval([uploadProgress]);
      progressSubscriberRef.current({ pollingInterval: newPollingInterval });
    }
  }, [uploadProgress]);

  // Upload handler
  const createTradeDataFn = async (param: DropzoneTypes[]) => {
    if (param.length === 0) {
      return show("Please upload a file", "", "error");
    }

    const uploadedFile = param[0];
    if (!uploadedFile.fileUrl) {
      return show("File upload incomplete", "", "error");
    }

    const payload: CreateTradeDataDTO["tradeData"] = {
      tradeDataFile: uploadedFile.fileUrl,
      ...(user?.userRole &&
        [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
          clientId: selectedClient?._id,
        }),
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
    };

    try {
      await postTradeData({ tradeData: { ...payload } }).unwrap();
      show("Data successfully uploaded", "", "success");
      setShowUploadDataModal(false);
      await pullProgress();
    } catch (error: any) {
      console.error("Upload error:", error);
      show(
        error?.data?.errors?.[0]?.message ||
          "Failed to upload data. Please try again.",
        "",
        "error"
      );
    }
  };

  // Line column options - matching all columns from AceDataView
  const lineColumnOptions = [
    { key: "entrySummaryLineNumber", label: "Line #" },
    { key: "htsNumberFull", label: "HTS Code" },
    { key: "manufacturerID", label: "Manufacturer ID" },
    { key: "countryOfOriginCode", label: "Origin" },
    { key: "countryOfExportCode", label: "Country of Export" },
    { key: "commercialDescription", label: "Commercial Description" },
    { key: "lineTariffGoodsValueAmount", label: "Goods Value" },
    { key: "totalLineDuties", label: "Line Duty Amount" },
    { key: "filerCode", label: "Filer Code" },
    { key: "portOfEntryCode", label: "Port of Entry" },
    { key: "entrySummaryDate", label: "Entry Summary Date" },
    { key: "entrySummaryCreateDate", label: "Entry Summary Create Date" },
    { key: "textileCategoryCode", label: "Textile Category" },
    { key: "relatedPartyIndicator", label: "Related Party" },
    { key: "ch99CodeList", label: "CH99 Code List" },
    { key: "mfnDuty", label: "MFN Duties" },
    { key: "duty201", label: "Section 201 Duties" },
    { key: "duty232", label: "Section 232 Duties" },
    { key: "duty301", label: "Section 301 Duties" },
    { key: "oth99Duties", label: "Other 99 Duties" },
  ];

  // Render skeleton rows while loading
  const renderSkeletonRows = () => {
    return Array.from({ length: limit }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        {summaryColumns.map((col) => (
          <TableCell key={col.key}>
            <Skeleton variant="rectangular" height={24} />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  if (shouldSkip && isAdmin) {
    return (
      <Stack sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Typography sx={{ fontSize: 16, fontWeight: tokens.fontWeight.semibold, color: tokens.text.secondary }}>
          Please select a client to view the entries
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      {/* Entry count */}
      <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, px: 3, pt: 2, pb: 0.5, gap: 1, bgcolor: tokens.bg.muted }}>
        <Typography sx={{ fontSize: tokens.fontSize.md, color: tokens.text.secondary, whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{totalEntries.toLocaleString()}</Box>
          {' '}{totalEntries === 1 ? 'entry' : 'entries'}
        </Typography>
        {(isLoading || isFetching) && (
          <CircularProgress size={14} sx={{ color: tokens.text.muted }} />
        )}
      </Stack>

      {/* Toolbar row */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flexShrink: 0, px: 3, pt: 1, pb: 2, gap: 1.5, bgcolor: tokens.bg.muted }}
      >
        {/* Filter & Sort */}
        <EntrySummaryFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          availablePorts={availablePorts}
          availableCountries={availableCountries}
          availableManufacturers={availableManufacturers}
          availableFilerCodes={availableFilerCodes}
          availableHtsCodes={availableHtsCodes}
          availableLiquidationStatuses={availableLiquidationStatuses}
        />

        {/* Line Columns */}
        <Button
          variant="outlined"
          startIcon={<ColumnsIcon sx={{ fontSize: 18 }} />}
          onClick={handleColumnsMenuOpen}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            bgcolor: tokens.bg.surface,
            '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 36,
            px: 2,
          }}
        >
          Line Columns
        </Button>

        {/* Export CSV */}
        <Button
          variant="outlined"
          startIcon={<ExportIcon sx={{ fontSize: 18 }} />}
          onClick={() => setShowExportModal(true)}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            bgcolor: tokens.bg.surface,
            '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.md,
            height: 36,
            px: 2,
          }}
        >
          Export CSV
        </Button>

        {/* Upload Data - pushed right */}
        {canUploadData && (
          <Button
            variant="contained"
            startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
            onClick={() => setShowUploadDataModal(true)}
            sx={{
              ml: 'auto',
              bgcolor: tokens.brand.primary,
              '&:hover': { bgcolor: tokens.brand.primaryHover },
              borderRadius: tokens.radius.md,
              fontWeight: tokens.fontWeight.bold,
              fontSize: tokens.fontSize.md,
              height: 36,
              px: 2,
              boxShadow: 'none',
            }}
          >
            Upload Data
          </Button>
        )}

        {!!columnsMenuAnchor && <Backdrop onClick={handleColumnsMenuClose} />}
      </Stack>

      {/* Columns Menu - for expanded line columns */}
      <StyledMenuPopover
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          width: "300px",
          "& .MuiPaper-root": { maxHeight: "400px", overflow: "auto" },
        }}
        onClose={handleColumnsMenuClose}
        open={!!columnsMenuAnchor}
        anchorEl={columnsMenuAnchor}
      >
        <Stack>
          <Typography variant="caption" sx={{ color: tokens.text.secondary, mb: 1, px: 2 }}>
            Columns shown in expanded line details
          </Typography>
          {lineColumnOptions.map((col) => (
            <Stack
              key={col.key}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body1"
                fontWeight={tokens.fontWeight.semibold}
                sx={{
                  cursor: "default !important",
                  background: "inherit !important",
                }}
              >
                {col.label}
              </Typography>
              <IOSSwitch
                disabled={
                  visibleLineColumns.length === 1 &&
                  visibleLineColumns.includes(col.key)
                }
                checked={visibleLineColumns.includes(col.key)}
                onChange={() => handleToggleLineColumn(col.key)}
              />
            </Stack>
          ))}
        </Stack>
      </StyledMenuPopover>

      {/* Upload Progress */}
      {(uploadProgress?.progress || uploadProgress?.progress === 0) &&
        uploadProgress.uploadTime !== null &&
        uploadProgress?.status === "inprocess" && (
          <Box sx={{ px: 3 }}>
            <ProcessBox
              progress={Math.round(uploadProgress.progress)}
              onClick={() => {}}
              task={uploadProgress}
            />
          </Box>
        )}

      {/* Upload Error */}
      {uploadProgress?.status === "error" && (
        <Box sx={{ maxWidth: 500, margin: "20px auto" }}>
          <PlaceholderCard
            title="Task Failed"
            description="An error occurred while processing your data. Please check your file and try again."
            imgUrl={crash_warning}
            isBordered
            centerContent
            cardStyles={{
              backgroundColor: "#fef2f2",
              borderColor: "#fecaca",
            }}
          />
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              marginTop: 2,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setTimeStamp(new Date().toISOString())}
              sx={{ color: tokens.text.secondary }}
            >
              Dismiss
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setShowUploadDataModal(true)}
              sx={{
                backgroundColor: "#d32f2f",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      )}

      {/* Table */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mx: 3,
          mb: 2,
        }}
      >
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            ...tableContainerSx,
          }}
        >
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 900, ...tableSx }}>
              <TableHead>
                <TableRow>
                  {summaryColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={{ width: col.width }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  renderSkeletonRows()
                ) : entrySummaries.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={SUMMARY_COLUMN_COUNT}
                      sx={{ textAlign: "center", py: 4 }}
                    >
                      <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                        No entries found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  entrySummaries.map((summary) => (
                    <EntrySummaryRowComponent
                      key={summary.entrySummaryNumber}
                      summary={summary}
                      isExpanded={expandedEntries.has(summary.entrySummaryNumber)}
                      onToggle={() => toggleEntry(summary.entrySummaryNumber)}
                      onDocumentsClick={handleDocumentsClick}
                      onStatusClick={handleStatusClick}
                      columnCount={SUMMARY_COLUMN_COUNT}
                      visibleLineColumns={visibleLineColumns}
                      summaryColumns={summaryColumns}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination — inside card */}
          {totalEntries > 0 && (
            <Pagination
              page={page}
              limit={limit}
              total={totalEntries}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </Stack>
      </Box>

      {/* Documents Drawer */}
      <DocumentsDrawer
        open={documentsDrawer.open}
        onClose={handleDocumentsDrawerClose}
        entrySummaryNumber={documentsDrawer.summary?.entrySummaryNumber ?? ""}
        clusterId={documentsDrawer.summary?.clusterId}
        documentCount={documentsDrawer.summary?.documentCount ?? 0}
        latestAuditStatus={documentsDrawer.summary?.latestAuditStatus}
        tenantId={isTDAdmin && selectedTenant ? selectedTenant._id : undefined}
      />

      {/* Audit Detail Modal */}
      <AuditDetailModal
        open={auditModal.open}
        onClose={handleAuditModalClose}
        audit={auditModal.audit}
        loading={auditModal.loading}
        entryNumber={auditModal.entrySummaryNumber}
      />

      {/* Export Modal */}
      {showExportModal && (
        <TradeDataExportModal show={showExportModal} setShow={setShowExportModal} />
      )}

      {/* Upload Data Modal */}
      {showUploadDataModal && (
        <UploadData
          title="Upload Trade Data"
          subTitle="Upload your ACE files so we can compile and structure your trade data for analysis"
          show={showUploadDataModal}
          setShow={setShowUploadDataModal}
          buttonText="Confirm"
          buttonAction={(e) => createTradeDataFn(e)}
          btnLoader={isCreatingTradeData}
          setTimeStamp={setTimeStamp}
        />
      )}
    </Stack>
  );
};

export default EntrySummaryView;
